<script setup>
import dayjs from 'dayjs';
import { DEV_MODE } from '@/config/dev-mode';
import { useAnalysisStore } from '@/store/modules/analysis';
import { useAnalysisDataStore } from '@/store/modules/analysisDataStore';
import { addContentLogAPI } from '@/api/logServiceApi';
import { fetchBaseServiceAPI } from '@/api/baseServiceApi.js';
import { isObjectEmpty } from '@/composables/functions';
import { CONTENTS as YEARLY_FORTUNE_CONTENTS } from './constants';
import { calculateDDay } from '@/utils/dateFormatter';
/* icon */
import IconArrow08 from '@/components/svg/IconArrow08.vue';

const route = useRoute();
const analysisStore = useAnalysisStore();
const dataStore = useAnalysisDataStore();
const { sajuData } = storeToRefs(dataStore);

const props = defineProps({
  ticketCountData: {
    required: true,
  },
  yearlyFortuneLogData: {
    required: true,
  },
  hasPermission: {
    required: true,
  },
  loadingTicket: {
    required: true,
  },
  warningMessage: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['goPopAnalysis', 'warning']);

const yearlyFortuneLogData = computed(() => {
  if (!props.yearlyFortuneLogData?.length) return null;
  const yearlyFortuneData = props.yearlyFortuneLogData[0];
  return yearlyFortuneData;
});

const dDay = computed(() => calculateDDay(yearlyFortuneLogData.value?.expiryDate));

const formattedExpiryDate = computed(() => {
  if (!yearlyFortuneLogData.value?.expiryDate) return '';
  return dayjs(yearlyFortuneLogData.value.expiryDate).format('YYYY.MM.DD');
});

/** 목차 리스트 - YEARLY_FORTUNE_CONTENTS에서 빈 항목(id: 2) 제외하고 사용 */
const yearlyFortuneContentList = computed(() => {
  // YEARLY_FORTUNE_CONTENTS에서 빈 항목(id: 2)을 제외하고 목차에 표시
  // idx 0: id 1 (가이드 1)
  // idx 1-7: id 3-9 (컨텐츠 1-7)
  return YEARLY_FORTUNE_CONTENTS.filter((item) => item.title !== '');
});

const goPopAnalysis = () => emit('goPopAnalysis');

const isChecking = ref(false);

/** 신년운세 데이터 미리 fetch하여 경고 여부 확인 */
const prefetchAndCheckWarning = async () => {
  if (dataStore.yearlyFortuneWarning) return true;
  if (!isObjectEmpty(dataStore.yearlyFortuneData)) return false;
  if (!sajuData.value || isObjectEmpty(sajuData.value)) return false;

  isChecking.value = true;
  try {
    const guestId = route.params.id;
    const params = {
      guestUserId: guestId,
      lang: 'KOR',
      userName: sajuData.value.inputdata.name ?? '',
      bln: sajuData.value.transdata.lucknum ?? null,
      gender: Number(sajuData.value.inputdata.gender) ?? null,
      unknownTime: sajuData.value.inputdata.unknown_time ?? null,
      analysisTypeId: 9,
      suggestionFlag: props.hasPermission ? '3' : '1',
      year: new Date().getFullYear(),
    };
    const result = await fetchBaseServiceAPI(params);
    const data = result.data || result.rag_data;
    if (data?.['경고']) {
      const message = data['경고']['메시지'] || '운세 분석이 어렵습니다.';
      dataStore.setYearlyFortuneWarning(message);
      return true;
    }
    if (data) {
      dataStore.setYearlyFortuneData(data);
      dataStore.setYearlyFortuneSubData({});
    }
    return false;
  } catch (e) {
    DEV_MODE && console.error('overviewYearlyFortune prefetch 실패:', e);
    return false;
  } finally {
    isChecking.value = false;
  }
};

/** 목차 인덱스를 contentIdx로 변환 */
const getContentIdxFromListIdx = (listIdx) => {
  // yearlyFortuneContentList의 idx가 곧 YEARLY_FORTUNE_SECTION_PAGE_MAP의 키
  // idx 0 → 가이드 (1페이지)
  // idx 1 → Contents01 (3페이지)
  // idx 2 → Contents04 (6페이지)
  // ...
  return listIdx;
};

/** 목차 이동 */
const goToChildMenu = async (listIdx) => {
  // 목차 인덱스를 contentIdx로 변환
  const contentIdx = getContentIdxFromListIdx(listIdx);

  addContentLogAPI({
    contentType: props.hasPermission ? 'ayf_fortune_toc_paid_opt3' : 'ayf_fortune_toc_free_opt3',
    option3: String(listIdx),
  });

  // 가이드1(idx=0) 이외 진입 시, 데이터가 없으면 먼저 fetch하여 경고 여부 확인
  if (contentIdx > 0) {
    const hasWarning = await prefetchAndCheckWarning();
    if (hasWarning) {
      emit('warning', dataStore.yearlyFortuneWarning);
      return;
    }
  }

  // 목차 첫 번째 항목(idx=0)은 가이드 페이지로 이동
  // YEARLY_FORTUNE_SECTION_PAGE_MAP: 0→1(가이드1), 1→2(가이드2), 2→3(컨텐츠1)...
  if (contentIdx > 1 && !props.hasPermission) {
    goPopAnalysis();
    return;
  }
  analysisStore.navigateToContent('/yearly-fortune', contentIdx);
  DEV_MODE && console.log('목차 이동', listIdx, '→ contentIdx:', contentIdx);
};
</script>

<template>
  <div class="si-overview si-overview--yearly">
    <!-- 로딩 오버레이 -->
    <div v-if="isChecking" class="overview-loading-overlay">
      <div class="overview-loading-spinner"></div>
    </div>
    <div class="si-overview__cover yearly-fortune-cover">
      <div class="si-overview__cover-inner">
        <div class="si-overview__header">
          <div class="si-overview__title">
            <div class="si-overview__name">
              <span class="si-overview__name-text">{{ analysisStore.userName || '-' }}</span
              ><span class="si-overview__name-suffix">님의</span>
            </div>
            <div class="si-overview__service">
              병오년(<span class="hanja-accent">丙午</span>年) 신년운세
            </div>
          </div>
          <div class="si-overview__badge">
            <template v-if="!loadingTicket && hasPermission">
              <span class="si-overview__badge-tag" v-if="yearlyFortuneLogData?.expiryDate">
                {{ dDay }}
              </span>
              <span class="si-overview__badge-tag" v-else>소장용</span>
              <span class="si-overview__badge-date" v-if="yearlyFortuneLogData?.expiryDate">
                ~ {{ formattedExpiryDate }} 까지
              </span>
            </template>

            <template v-if="!loadingTicket && !hasPermission">
              <span class="si-overview__badge-tag">구매 후 이용가능</span>
            </template>
          </div>
        </div>

        <div class="si-overview__img">
          <div class="yearly-fortune-cover-visual">
            <div class="yearly-fortune-cover-main">
              <div class="yearly-fortune-cover-bg-line"></div>
              <div class="yearly-fortune-cover-window"></div>
              <div class="yearly-fortune-cover-ticket"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="si-overview__toc">
      <div class="si-overview__toc-title">목차</div>
      <div class="si-overview__toc-list">
        <button
          v-for="(item, idx) in yearlyFortuneContentList"
          :key="idx"
          class="si-overview__toc-item"
          :class="{ 'si-overview__toc-item--locked': !hasPermission }"
          @click="goToChildMenu(idx)"
        >
          <span>{{ item.title }}</span>
          <div class="si-overview__toc-icon">
            <IconArrow08 />
          </div>
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
@import '@/assets/nangman_style/_saju-insight-overview.scss';
</style>

<style scoped lang="scss">
.si-overview {
  position: relative;
}

.overview-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.overview-loading-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid #ebebeb;
  border-top-color: #be3331;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/** 신년운세 고유: 커버 패딩 override (이미지가 전체 너비 사용) */
.yearly-fortune-cover {
  padding: 80px 0 40px;
  overflow: hidden;

  .si-overview__header {
    padding: 0 40px;
  }
}

.hanja-accent {
  color: #b32f24;
}

/** 신년운세 고유: 커버 비주얼 */
.yearly-fortune-cover-visual {
  position: relative;
  width: 100%;
  height: 100%;
}

.yearly-fortune-cover-main {
  position: absolute;
  top: 2%;
  left: 50%;
  transform: translateX(-50%);
  width: 70%;
  max-width: 395px;
  aspect-ratio: 395 / 501;

  .yearly-fortune-cover-bg-line {
    position: absolute;
    top: 15%;
    left: 50%;
    transform: translateX(-50%) rotate(0deg);
    width: 150%;
    aspect-ratio: 598 / 339;
    background: url('@manseImages/yearly-fortune/img_cover_bg_line.png') center center no-repeat;
    background-size: contain;
    pointer-events: none;
    z-index: 0;
  }

  .yearly-fortune-cover-window {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: url('@manseImages/yearly-fortune/img_cover_window.png') center center no-repeat;
    background-size: contain;
    z-index: 2;
  }

  .yearly-fortune-cover-ticket {
    position: absolute;
    top: 53%;
    left: -5%;
    width: 80%;
    aspect-ratio: 211 / 320;
    background: url('@manseImages/yearly-fortune/img_cover_ticket.png') center center no-repeat;
    background-size: contain;
    z-index: 3;
  }
}
</style>
